import React, { useState } from 'react';
import { ReactComponent as SafetyMonitoringSVG  } from '../../assets/group-c/1.SafetyMonitoring.svg';
import { ReactComponent as PowerDistributionSVG } from '../../assets/group-c/2.PowerDistribution.svg';
import { ReactComponent as CommsTowerSVG         } from '../../assets/group-c/3.CommsTower.svg';
import { ReactComponent as DataLoggingSVG        } from '../../assets/group-c/4.DataLogging.svg';

// ─── State colours ────────────────────────────────────────────────────────────
const STATE_COLOURS = {
  offline:  '#f59e0b',
  selected: '#60a5fa',
  online:   '#22c55e',
};

const DEFAULT_STATES = {
  safety_monitoring:  'offline',
  power_distribution: 'offline',
  comms_tower:        'offline',
  data_logging:       'offline',
};

// ─── Zone layout ──────────────────────────────────────────────────────────────
// 2×2 grid in a 900×680 viewBox.
// Each tile: 440×330 with 10px gap and 5px outer margin.
// dotIds: zone-specific IDs on the indicator dome paths in each SVG file.
// accentIds: adjacent dome accent paths that also need recoloring (if any).
const ZONES = [
  {
    id: 'safety_monitoring',  label: 'Safety Monitoring',
    SvgComponent: SafetyMonitoringSVG,
    dotIds: ['sm-status-dot', 'sm-dot-accent'],
    x: 5,   y: 5,   w: 440, h: 330,
  },
  {
    id: 'power_distribution', label: 'Power Distribution',
    SvgComponent: PowerDistributionSVG,
    dotIds: ['pd-status-dot'],
    x: 455, y: 5,   w: 440, h: 330,
  },
  {
    id: 'comms_tower',        label: 'Comms Tower',
    SvgComponent: CommsTowerSVG,
    dotIds: ['ct-status-dot'],
    x: 5,   y: 345, w: 440, h: 330,
  },
  {
    id: 'data_logging',       label: 'Data Logging',
    SvgComponent: DataLoggingSVG,
    dotIds: ['dl-status-dot'],
    x: 455, y: 345, w: 440, h: 330,
  },
];

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * SubstationIsometric — individual SVG art per zone in a 2×2 grid.
 *
 * Props:
 *   zoneStates  — { safety_monitoring, power_distribution, comms_tower,
 *                   data_logging } each: 'offline' | 'selected' | 'online'
 *   onZoneClick — (zoneId: string) => void
 *   queueOrder  — { [zoneId]: number | null } — 1-based queue position, null if not queued
 */
function SubstationIsometric({ zoneStates = DEFAULT_STATES, onZoneClick, queueOrder = {} }) {
  const states = { ...DEFAULT_STATES, ...zoneStates };
  const [hoveredZone, setHoveredZone] = useState(null);

  // Build one consolidated <style> block at root level so rules apply globally
  // regardless of nested SVG scoping in different browsers.
  const dotStyles = ZONES.map(({ id, dotIds }) => {
    const fill = STATE_COLOURS[states[id]];
    return dotIds.map(dotId => `#${dotId} { fill: ${fill} !important; }`).join('\n');
  }).join('\n');

  return (
    <svg
      viewBox="0 0 900 680"
      xmlns="http://www.w3.org/2000/svg"
      style={{ display: 'block', width: '100%' }}
      role="img"
      aria-label="Substation triage interface — four zones"
    >
      {/* All dot-recolor rules hoisted to root SVG so they aren't scoped to a nested SVG */}
      <style>{dotStyles}</style>

      {ZONES.map(({ id, label, SvgComponent, x, y, w, h }) => {
        const state    = states[id];
        const isQueued = queueOrder[id] != null;
        const qNum     = queueOrder[id];

        // Label fades when zone is selected/online (user knows which zone it is via highlight)
        const labelOpacity = state === 'offline' ? 0.85 : 0.22;

        return (
          <g key={id}>
            {/* ── Nested SVG tile ── */}
            <svg x={x} y={y} width={w} height={h} overflow="hidden">
              <SvgComponent
                width="100%"
                height="100%"
                preserveAspectRatio="xMidYMid slice"
                style={state === 'offline'
                  ? { filter: 'brightness(0.65) saturate(0.55)' }
                  : undefined}
              />
            </svg>

            {/* Offline: subtle dark tint — lighter than before so amber dot stays readable */}
            {state === 'offline' && (
              <rect
                x={x} y={y} width={w} height={h}
                fill="black" opacity="0.12"
                pointerEvents="none"
              />
            )}

            {/* Selected: blue glow border */}
            {state === 'selected' && (
              <rect
                x={x} y={y} width={w} height={h}
                fill="rgba(96,165,250,0.08)"
                stroke="#60a5fa" strokeWidth="3"
                pointerEvents="none"
                style={{ filter: 'drop-shadow(0 0 8px #60a5fa)' }}
              />
            )}

            {/* Online: green glow border */}
            {state === 'online' && (
              <rect
                x={x} y={y} width={w} height={h}
                fill="rgba(34,197,94,0.06)"
                stroke="#22c55e" strokeWidth="2"
                pointerEvents="none"
                style={{ filter: 'drop-shadow(0 0 6px #22c55e)' }}
              />
            )}

            {/* Zone label — top-left overlay, fades when selected/online */}
            <g pointerEvents="none" opacity={labelOpacity}>
              <rect
                x={x + 8} y={y + 8}
                width={label.length * 6.4 + 12} height={18}
                rx={3}
                fill="rgba(0,0,0,0.55)"
              />
              <text
                x={x + 14} y={y + 20}
                fontSize="10"
                fontFamily="system-ui, sans-serif"
                fontWeight="600"
                fill="#e2e8f0"
                letterSpacing="0.04em"
                style={{ textTransform: 'uppercase' }}
              >
                {label}
              </text>
            </g>

            {/* Queue position badge — shown when zone is in the queue */}
            {isQueued && (
              <g pointerEvents="none">
                {/* Sequence number watermark — outlined for legibility over zone art */}
                <text
                  x={x + w / 2}
                  y={y + h / 2 + 28}
                  textAnchor="middle"
                  fontSize="120"
                  fontFamily="system-ui, sans-serif"
                  fontWeight="900"
                  fill={state === 'online' ? '#22c55e' : '#60a5fa'}
                  stroke="rgba(0,0,0,0.72)"
                  strokeWidth="10"
                  paintOrder="stroke"
                  opacity="0.72"
                  letterSpacing="-4"
                >
                  {qNum}
                </text>
                {/* Smaller, more readable badge in bottom-right corner */}
                <rect
                  x={x + w - 30} y={y + h - 26}
                  width={24} height={20}
                  rx={4}
                  fill={state === 'online' ? 'rgba(34,197,94,0.25)' : 'rgba(96,165,250,0.25)'}
                  stroke={state === 'online' ? '#22c55e' : '#60a5fa'}
                  strokeWidth="1"
                />
                <text
                  x={x + w - 18}
                  y={y + h - 11}
                  textAnchor="middle"
                  fontSize="11"
                  fontFamily="system-ui, sans-serif"
                  fontWeight="700"
                  fill={state === 'online' ? '#22c55e' : '#60a5fa'}
                >
                  #{qNum}
                </text>
              </g>
            )}

            {/* Hover highlight */}
            {hoveredZone === id && (
              <rect
                x={x} y={y} width={w} height={h}
                fill="rgba(255,255,255,0.07)"
                stroke="#94a3b8" strokeWidth="1.5"
                pointerEvents="none"
              />
            )}
          </g>
        );
      })}

      {/* ── Hit targets (invisible, full tile) ── */}
      <g id="hit-targets">
        {ZONES.map(({ id, x, y, w, h }) => (
          <rect
            key={id}
            id={`hittarget-${id}`}
            x={x} y={y} width={w} height={h}
            fill="transparent"
            stroke="none"
            pointerEvents="all"
            style={{ cursor: 'pointer' }}
            onClick={onZoneClick ? () => onZoneClick(id) : undefined}
            onMouseEnter={() => setHoveredZone(id)}
            onMouseLeave={() => setHoveredZone(null)}
          />
        ))}
      </g>

      {/* ── Zone tooltips ── */}
      {ZONES.map(({ id, label, x, y, w, h }) => {
        if (hoveredZone !== id) return null;
        const isQueued   = queueOrder[id] != null;
        const actionText = isQueued ? 'Click to remove from queue' : 'Click to add to queue';
        const boxW       = actionText.length * 6.2 + 24;
        const boxH       = 26;
        const cx         = x + w / 2;
        const bx         = Math.max(x + 4, Math.min(x + w - boxW - 4, cx - boxW / 2));
        const by         = y + h - boxH - 10;
        const accentCol  = isQueued ? '#f87171' : '#22d3ee';
        return (
          <g key={`tip-${id}`} pointerEvents="none">
            <rect x={bx} y={by} width={boxW} height={boxH} rx={5}
              fill="rgba(2,6,23,0.92)" stroke={accentCol} strokeWidth="1.2" />
            <text x={bx + boxW / 2} y={by + 17}
              textAnchor="middle" fontSize="10" fontFamily="system-ui, sans-serif"
              fontWeight="600" fill={accentCol} letterSpacing="0.02em">
              {actionText}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

export default SubstationIsometric;
